<template>
	<div>
		<component
			:is="isBoxed ? 'j-card' : 'div'"
			:class="{ 'pr6 pl6 pt6 bg5': isBoxed }"
		>
			<!-- ########################################## -->
			<!-- Schema based rendering of all fields -->
			<!-- ########################################## -->

			<div
				v-for="(field, i) in computedSchema"
				:key="i"
				:class="[`mb${gap}`, { 'b2 bcWarning p2 r2': isGodMode }]"
			>
				<!-- <pre>{{ field }}</pre> -->
				<form-field
					:name="field.bindTo || Math.random().toString() + i"
					:componentName="field.component"
					:label="field.label"
					v-bind="field.props"
					:size="size"
					:field="field"
					v-model="form[field.bindTo]"
					@input="handleFieldUpdate(field, 'input', $event)"
					@remove="handleFieldUpdate(field, 'remove', $event)"
					@blur="handleFieldUpdate(field, 'blur', $event)"
				/>

				<!-- ########################################## -->
				<!-- God mode will allow to see data bindings + special buttons for liquid generation -->
				<!-- ########################################## -->
				<template v-if="isGodMode">
					<div
						class="xs-12 bg7--l5 p4 r2 mt1"
						v-if="field.component !== 'html-box'"
					>
						<div class="lh125">
							<span class="fw7">data binding:</span>
							{{ field.bindTo }}
						</div>
						<div class="lh125" v-if="field.relation">
							<span class="fw7">reference:</span>
							{{ field.relation }}
						</div>
						<div class="lh125 mt2">
							<j-button
								@click="handleLiquidGeneration(field, 'cases')"
								size="xsmall"
								type="bordered"
								>Get Case Liquid</j-button
							>
							<!-- NOTE: not sure where this should point -->
							<!-- <j-button
								class="ml4"
								@click="handleLiquidGeneration(field, 'policies')"
								size="xsmall"
								type="bordered"
							>
								Get Policy Liquid
							</j-button>-->
						</div>
					</div>
				</template>
			</div>
		</component>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import FormField from "./FormField"
import JButton from "@/components/JButton"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import get from "lodash.get"
import { computeSchema } from "./computeSchema.js"
import { generateLiquid } from "./utils/liquidGenerator.js"
import addFactToCase from "./addFactToCase.js"
import updateFactOfCase from "./updateFactOfCase.js"
import updateCase from "./updateCase.js"

export default {
	components: { JCard, FormField, JButton },
	props: {
		/**
		 * Schema for the form
		 */
		schema: {
			type: Array,
			required: true,
			default: () => []
		},

		headline: {
			type: String,
			default: ""
		},

		/**
		 * Whether to encapsulate the whole form in a box
		 */
		isBoxed: {
			type: Boolean,
			default: true
		},

		/**
		 * Size of the form inputs
		 */
		size: {
			validator(val) {
				return ["xsmall", "small", "medium", "large"].includes(val)
			},
			default: "medium"
		},

		/**
		 * Vertical gap between fields
		 * It translates into mb{gap} class
		 */
		gap: {
			type: String,
			default: "6"
		}
	},

	data() {
		return {
			isLoading: false,
			form: {}
		}
	},

	computed: {
		/**
		 * Schema with proper component names, added model
		 */
		computedSchema() {
			return computeSchema(this.schema)
		},

		/**
		 * God mode feature on/off
		 */
		isGodMode() {
			return this.$store.state.caseManagement.isGodMode
		}
	},

	methods: {
		// API calls
		addFactToCase,
		updateFactOfCase,
		updateCase,

		/**
		 * It will go through schema and it will populate form with data from store
		 */
		populateFormFromStore() {
			const form = {}
			this.computedSchema.forEach(field => {
				form[field.bindTo] = this.readCache(field)
			})
			this.form = form
		},

		/**
		 * Based on what kind of component it is, we do upsertField differently
		 * E.g. j-base-input should call API only on blur but j-base-select should call API on input
		 * We also handle custom injected components in field.componentObject
		 */
		handleFieldUpdate(field, eventName, payload) {
			// 1: First handle custom injected component if there is settings for upsert in `field.componentObject.upsertFieldOn`
			if (field?.componentObject?.upsertFieldOn === eventName)
				return this.upsertField({ field, payload })

			// 2: Handle normal components that are defined in componentsDictionary
			// Components that should trigger update on blur
			const updateOnBlurComponents = [
				"j-base-input-text",
				"j-base-input-long-text",
				"j-base-input-number"
			]

			// Component that should trigger updates on blur
			if (eventName === "blur") {
				if (updateOnBlurComponents.includes(field.component))
					return this.upsertField({ field, payload })
			}
			// Component that should trigger updates on input
			else if (eventName === "input") {
				if (!updateOnBlurComponents.includes(field.component))
					return this.upsertField({ field, payload, eventName })
			}
			// Component that should trigger updates on remove. E.g : EntitySelect (for insureds)
			else if (eventName === "remove") {
				if (!updateOnBlurComponents.includes(field.component))
					return this.upsertField({ field, payload, eventName })
			}
		},

		upsertField({ field, payload, eventName }) {
			// Holder
			if (field?.bindTo === "case.holder")
				return this.handlerUpdateHolder(field)

			// Insureds
			if (field?.bindTo?.includes("case.insureds["))
				return this.handlerUpdateInsureds({ field, payload, eventName })

			// Case Facts
			if (field?.bindTo?.includes("case.facts"))
				return this.handlerUpsertCaseFact(field)
		},

		async handlerUpsertCaseFact(field) {
			const cache = this.$store.state.caseManagement.case
			const payload = {
				type: field.dynamicType,
				value: {
					[`${field.dataType || "string"}Value`]: this.form[field.bindTo]
				}
			}

			// Find field in store
			// Based on this, we will decide whether add or update
			const foundField = cache?.facts?.find(
				fact => fact.type === field.dynamicType
			)
			// ########################
			// ADD
			// ########################
			if (!foundField) {
				const res = await addFactToCase({
					caseId: this.$store.state.caseManagement.caseId,
					input: payload
				})

				// Errors
				if (res?.data?.addFactToCase?.status !== "success") {
					this.showError(field.label)
					this.populateFormFromStore() // revert form
					return Promise.resolve(res)
				}

				// Success - update store data
				this.$store.commit("caseManagement/ADD_CASE_FACT", {
					id: res?.data?.addFactToCase?.createdStatus?.id,
					...payload
				})

				return Promise.resolve(res)
			}
			// ########################
			// Update
			// ########################
			else {
				const res = await updateFactOfCase({
					caseId: this.$store.state.caseManagement.caseId,
					factId: foundField.id,
					input: payload
				})

				// Errors
				if (res?.data?.updateFactOfCase?.status !== "success") {
					this.showError(field.label)
					this.populateFormFromStore() // revert form
					return Promise.resolve(res)
				}

				// Update store data when success
				this.$store.commit("caseManagement/UPDATE_CASE_FACT", {
					id: foundField.id,
					...payload
				})
				return Promise.resolve(res)
			}
		},

		async handlerUpdateHolder(field) {
			const { caseId } = this.$store.state.caseManagement

			this.isLoading = true
			const res = await updateCase({
				id: caseId,
				input: { holderId: this.form[field.bindTo] }
			})

			// Errors
			if (res?.data?.updateCase?.status !== "success") {
				this.showError(field.label)
				this.populateFormFromStore() // revert form
				this.isLoading = false
				return Promise.resolve(res)
			}

			// Success - update cache by refetching the whole case
			const refetchedCase = await this.$store.dispatch(
				"caseManagement/fetchCase",
				{ caseId }
			)

			// Refresh internal state of the form
			this.populateFormFromStore()
			this.isLoading = false
			return Promise.resolve(res)
		},

		async handlerUpdateInsureds({ field, payload, eventName }) {
			const { caseId } = this.$store.state.caseManagement

			const insuredIndex = field.bindTo.charAt(14) // Insured index is at char 14 because this function is only called when bindTo starts with 'case.insureds[' ...

			// Go through store and get all insuredIds
			const insuredIds =
				this.$store.state?.caseManagement?.case?.insureds?.map(
					insured => insured.id
				) || []

			// If remove event, just remove the id from insuredIds
			// If input event, check if it is a new one or updating an existing one
			if (eventName === "remove") {
				insuredIds.splice(insuredIds.indexOf(payload), 1)
			} else if (eventName === "input") {
				if (insuredIndex >= insuredIds.length) insuredIds.push(payload)
				else insuredIds[insuredIndex] = payload
			}

			// Update call
			this.isLoading = true
			const res = await updateCase({
				id: caseId,
				input: { insuredIds }
			})

			// Errors
			if (res?.data?.updateCase?.status !== "success") {
				this.showError(field.label)
				this.populateFormFromStore() // revert form
				this.isLoading = false
				return Promise.resolve(res)
			}

			// Success - update cache by refetching the whole case
			const refetchedCase = await this.$store.dispatch(
				"caseManagement/fetchCase",
				{ caseId }
			)

			this.isLoading = false
			return Promise.resolve(res)
		},

		/**
		 * It will make sure that fields with defaultValue will have default value
		 * We are invoking this only for case facts that are not references
		 */
		async handlerDefaultValues() {
			// We are not doing defaultValue for these components
			const notInvolvedComponents = [
				"entity-contact",
				"entity-identity",
				"entity-fact",
				"entity-address",
				"entity-select",
				"entity-field"
			]

			// Collect all fields with defaultValue that should be processed
			const fieldsToProcess = this.computedSchema
				// Check if field has defaultValue
				.filter(field => field.defaultValue !== undefined)
				// Check if we support this component for default value of case fact
				// TODO: most likely needs to be updated when we inject custom compo
				.filter(field => !notInvolvedComponents.includes(field.component))
				// If we don't have this fact in store case.facts yet, process it
				.map(field => {
					if (
						!this.$store.state.caseManagement.case?.facts?.find(
							fact => fact.type === field.dynamicType
						)
					) {
						// Populate form so that it's ready for upsertField call
						// upsertField takes value from internal state from this.form thus we need to populate it here
						this.form[field.bindTo] = field.defaultValue
						return field
					}
				})
				// get rid of nulls and undefineds
				.filter(item => item)

			// Call upsertField for all fields at once
			// NOTE: we might need to optimise this and do batching in case there is too many fields with default value which seems unlikely
			const res = await Promise.all([
				...fieldsToProcess.map(field => this.upsertField({ field }))
			])
		},

		/**
		 * Based on field it will return correct value from store
		 */
		readCache(field) {
			const cache = this.$store.state.caseManagement.case

			// Holder
			if (field?.bindTo === "case.holder") return cache?.holder?.id || ""

			// Insureds
			if (field?.bindTo?.includes("case.insureds["))
				return get(this.$store.state.caseManagement, field?.bindTo + ".id", "")

			// Case facts
			if (field?.bindTo?.includes("case.facts")) {
				return cache?.facts?.find(fact => fact.type === field.dynamicType)
					?.value?.[`${field.dataType || "string"}Value`]
			}
		},

		/**
		 * Helper function to add error message
		 */
		showError(fieldName) {
			this.$store.dispatch("addMessage", {
				delay: "3000",
				message: {
					type: "error",
					text: `"${fieldName}" wasn't saved, please update it again` // TODO: localize
				}
			})
		},

		/**
		 * Helper method to generate liquid syntax based on field
		 */
		handleLiquidGeneration(field, forWhat) {
			// Helper to save value to clipboard
			//  Not guaranteed to work everywhere, works in Chrome
			const copyToClipboard = str => {
				const el = document.createElement("textarea")
				el.value = str
				el.setAttribute("readonly", "")
				el.style.position = "absolute"
				el.style.left = "-9999px"
				document.body.appendChild(el)
				el.select()
				document.execCommand("copy")
				document.body.removeChild(el)
			}

			// Generate liquid
			const generatedLiquid = generateLiquid(field, forWhat)
			console.log({ field })
			console.log(generatedLiquid)

			// Save result to clipboard
			copyToClipboard(generatedLiquid)

			// Show message
			this.$store.dispatch("addMessage", {
				delay: 1000,
				message: {
					type: "neutral",
					text: "Copied to your clipboard!"
				}
			})
		}
	},

	// Anytime case updates we repopulate internal form state from cache based on schema
	// E.g. when we have 2 case-forms and both include holder email, we wanna have holder email updated anytime
	// it updates in one of those case-forms. Thus we react to any change in store.
	watch: {
		"$store.state.caseManagement.case": {
			handler() {
				this.populateFormFromStore()
			},
			deep: true,
			immediate: true
		}
	},

	created() {
		// Handle defaultValues when component is created
		this.handlerDefaultValues()
	}
}
</script>
